import type { ChangeEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { Info } from '@mui/icons-material';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { Input } from '@repo/videa-components/src/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { isEqual } from 'lodash';

import type { PreferenceKey } from '../../../../@types/preferences';
import { preferencesMetaData } from '../../../../@types/preferences';
import { PreferenceButton } from '../../../../common-components/composed/preferenceButton/PreferenceButton';
import { endpoints } from '../../../../react-query/endpoints';
import { usePreferences } from '../../../../react-query/usePreferences';
import { green400, red400 } from '../../../../theme/videa-style-tokens';
import {
  StoreKeys,
  useLocalStorage,
} from '../../../../utils/hooks/useLocalStorage';

const StyledTableCell = styled(TableCell)`
  &.has-text-green {
    color: ${green400};
  }
  &.has-text-red {
    color: ${red400};
  }
`;

export const PreferencesCustomizer = () => {
  const { serverData } = usePreferences();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [localStorageOverrides, setLocalStorageOverrides] = useLocalStorage<
    Record<string, any>
  >(StoreKeys.PREFERENCES_OVERRIDES_KEY, {});
  const [newOverrides, setNewOverrides] = useState(localStorageOverrides ?? {});

  const displayPrefs = useMemo(
    () => Object.entries(serverData ?? {}).sort(),
    [serverData],
  );

  const handleOpen = () => setIsOpen(true);

  const handleChangeSelect = useCallback(
    (flagName: string, possibleValuesType?: 'number' | 'string') =>
      (value: string) => {
        if (!newOverrides) return;

        if (value === 'none') {
          // If user selects the "None" option, then flag should be removed from overrides.
          const newValue = { ...newOverrides };
          delete newValue[flagName];
          setNewOverrides(newValue);
        } else if (value === 'true' || value === 'false') {
          const newValue = {
            ...newOverrides,
            [flagName]: value === 'true',
          };
          setNewOverrides(newValue);
        } else if (possibleValuesType) {
          const newValue = {
            ...newOverrides,
            [flagName]:
              possibleValuesType === 'number' ? parseInt(value) : value,
          };
          setNewOverrides(newValue);
        }
      },
    [newOverrides],
  );

  const handleChangeInput = useCallback(
    (flagName: string, originalValue: number | string) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        if (!newOverrides) return;

        if (event.target.value === '') {
          // If user clears the field, then flag should be removed from overrides.
          const value = { ...newOverrides };
          delete value[flagName];
          setNewOverrides(value);
        } else {
          const value = {
            ...newOverrides,
            [flagName]:
              typeof originalValue === 'number'
                ? parseInt(event.target.value)
                : event.target.value,
          };
          setNewOverrides(value);
        }
      },
    [newOverrides, setNewOverrides],
  );

  const getControl = useCallback(
    (flagName: string, flagValue: any) => {
      // `preferencesMetaData` contains preferences that are string (with fixed
      // set of options), number (with fixed set of options), boolean and
      // string, AND boolean and number.
      const preferenceMetaData = preferencesMetaData[flagName as PreferenceKey];
      if (preferenceMetaData) {
        const { type, possibleValues } = preferenceMetaData;
        return (
          <Select
            value={newOverrides?.[flagName]?.toString() ?? 'none'}
            onValueChange={handleChangeSelect(
              flagName,
              typeof possibleValues[0] === 'number' ? 'number' : 'string',
            )}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {type.includes('boolean') && (
                <SelectItem value="true">True</SelectItem>
              )}
              {type.includes('boolean') && (
                <SelectItem value="false">False</SelectItem>
              )}
              {possibleValues
                .filter((value) => value != null && value !== undefined)
                .map((value) => (
                  <SelectItem key={value} value={String(value)}>
                    {String(value)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        );
      }

      if (typeof flagValue === 'boolean') {
        return (
          <Select
            value={newOverrides?.[flagName]?.toString() ?? 'none'}
            onValueChange={handleChangeSelect(flagName)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        );
      } else if (
        typeof flagValue === 'string' ||
        typeof flagValue === 'number'
      ) {
        return (
          <div className="vai-space-y-2">
            <Input
              onChange={handleChangeInput(flagName, flagValue)}
              type={typeof flagValue === 'number' ? 'number' : 'text'}
              value={newOverrides?.[flagName] ?? ''}
              placeholder="Enter value"
            />
            {typeof flagValue === 'string' && (
              <p className="vai-text-xs vai-text-muted-foreground">
                Possible values are unknown. Dev may need to add entry to
                preferencesMetaData.
              </p>
            )}
          </div>
        );
      }
    },
    [handleChangeInput, handleChangeSelect, newOverrides],
  );

  const handleClickClear = useCallback(() => {
    setLocalStorageOverrides({});
    setNewOverrides({});
    queryClient.invalidateQueries([endpoints.preferences]);
  }, [queryClient, setLocalStorageOverrides]);

  const handleClickSave = useCallback(() => {
    setLocalStorageOverrides(newOverrides);
    queryClient.invalidateQueries([endpoints.preferences]);
  }, [newOverrides, queryClient, setLocalStorageOverrides]);

  const shouldAllowSave = !isEqual(newOverrides, localStorageOverrides);
  const shouldAllowClear = !!Object.entries(newOverrides).length;

  return (
    <>
      <PreferenceButton
        className="pref-button"
        onClick={handleOpen}
        startIcon={<Info />}
        data-testid="preferences-customizer-button"
      >
        Preferences Customizer
      </PreferenceButton>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="vai-max-w-[900px] vai-w-[90vw] vai-max-h-[95vh] vai-overflow-y-auto vai-gap-6 vai-p-6 vai-pt-0"
          hideClose
        >
          <DialogHeader className="vai-sticky vai-top-0 vai-bg-background vai-z-10 vai-border-b vai-pt-6 vai-pb-4 vai-bg-white">
            <DialogTitle className="vai-flex vai-justify-between vai-items-center">
              <span>
                <Trans>Preferences Customizer</Trans>
              </span>
              <div className="vai-flex vai-gap-2">
                <Button
                  disabled={!shouldAllowClear}
                  onClick={handleClickClear}
                  variant="outline"
                >
                  Clear All
                </Button>
                <Button disabled={!shouldAllowSave} onClick={handleClickSave}>
                  Save
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Flag Name</TableCell>
                  <TableCell>Server Value</TableCell>
                  <TableCell>Override Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serverData ? (
                  displayPrefs.map(([name, value]) => {
                    const control = getControl(name, value);
                    if (!control) return null;
                    return (
                      <TableRow key={name}>
                        <TableCell>{name}</TableCell>
                        <StyledTableCell
                          data-testid="flag-server-value"
                          className={classNames({
                            'has-text-green': value === true,
                            'has-text-red': value === false,
                          })}
                        >
                          {value.toString()}
                        </StyledTableCell>
                        <TableCell>{control}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <>
                    {[...Array(3)].map(() => (
                      <TableRow>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  );
};
